import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Text from "react-native-web/dist/exports/Text";
import View from "react-native-web/dist/exports/View";
import StackView from '../StackView';
import { applyTextStyles, useTheme, useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, variantProp, viewProps } from '../utils';
import { useViewport } from '../ViewportProvider';
import useCopy from '../utils/useCopy';
import Step from './Step';
import defaultDictionary from './dictionary';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const STYLE_BAR_VARIANT = 'bar';
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectContainerStyles = _ref => {
  let {
    containerPaddingBottom,
    containerPaddingLeft,
    containerPaddingRight,
    containerPaddingTop
  } = _ref;
  return {
    paddingBottom: containerPaddingBottom,
    paddingLeft: containerPaddingLeft,
    paddingRight: containerPaddingRight,
    paddingTop: containerPaddingTop
  };
};
const selectStepTrackerLabelContainerStyles = _ref2 => {
  let {
    labelMarginTop
  } = _ref2;
  return {
    marginTop: labelMarginTop
  };
};
const selectStepTrackerLabelStyles = (_ref3, themeOptions) => {
  let {
    labelColor,
    labelFontSize,
    labelFontWeight,
    labelFontName,
    labelLineHeight
  } = _ref3;
  return applyTextStyles({
    color: labelColor,
    fontSize: labelFontSize,
    lineHeight: labelLineHeight,
    fontWeight: labelFontWeight,
    fontName: labelFontName,
    themeOptions
  });
};
const selectStepsContainerStyles = _ref4 => {
  let {
    barGap
  } = _ref4;
  return {
    gap: barGap
  };
};

/**
 * StepTracker component shows the current position in a sequence of steps.
 *
 * ## Component API
 *
 * - `current` prop allows to control the current step, 0-based number;
 * - use `copy` and `dictionary` props to internationalize the labels;
 * - `steps` is and array of strings defining step titles;
 * - tokens and variant props define the appearance of the `StepTracker`:
 *
 * ## Usability and A11y guidelines
 *
 * Keep in mind that in its current implementation this is not an interactive
 * component and can't be used to navigate between steps. The application
 * must provide its own navigation mechanism and state control. That is the
 * main reason the component assumes the `progressbar` role in terms of
 * accessibility. This also makes it extremely important to make sure you
 * set a11y on controls used to modify the state of the step tracker.
 *
 * You can pass standard a11y props to the `StepTracker` component, including
 * the ones that are specific to the `progressbar` role. By default the
 * following props are used:
 *
 * - `accessibilityRole` (`role`): `progressbar`,
 * - `accessibilityLabel` (`aria-label`): `Step I of N: <Current Step Label>`,
 * - `accessibilityLevel` (`aria-level`): 1,
 * - `accessibilityValue.min` (`aria-valuemin`): `0`,
 * - `accessibilityValue.max` (`aria-valuemax`): `steps.length - 1`,
 * - `accessibilityValue.now` (`aria-valuenow`): `current`,
 * - `accessibilityValue.text` (`aria-valuetext`): `<Current Step Label>`,
 *
 */
const StepTracker = /*#__PURE__*/React.forwardRef((_ref5, ref) => {
  let {
    current = 0,
    copy = 'en',
    dictionary = defaultDictionary,
    steps = [],
    tokens,
    variant,
    ...rest
  } = _ref5;
  const isBarVariant = variant?.style === STYLE_BAR_VARIANT;
  const viewport = useViewport();
  const {
    showStepTrackerLabel,
    ...themeTokens
  } = useThemeTokens('StepTracker', tokens, variant, {
    viewport
  });
  const {
    textStepTrackerLabel
  } = themeTokens;
  const getCopy = useCopy({
    dictionary,
    copy
  });
  const stepTrackerLabel = showStepTrackerLabel ? (typeof copy === 'string' ? getCopy(textStepTrackerLabel ?? 1).stepTrackerLabel : getCopy('stepTrackerLabel')).replace('%{stepNumber}', current < steps.length ? current + 1 : steps.length).replace('%{stepCount}', steps.length).replace('%{stepLabel}', current < steps.length ? steps[current] : steps[steps.length - 1]) : getCopy('stepTrackerLabel');
  const getStepLabel = index => {
    if (themeTokens.showStepLabel) {
      return getCopy(index + 1)?.stepLabel.replace('%{stepNumber}', index + 1);
    }
    return '';
  };
  const {
    themeOptions
  } = useTheme();
  if (!steps.length) return null;
  const selectedProps = selectProps({
    accessibilityLabel: stepTrackerLabel,
    accessibilityRole: 'progressbar',
    accessibilityValue: {
      min: 0,
      max: steps.length - 1,
      now: current,
      text: steps[current]
    },
    ...rest
  });
  const stepsContainerAccessibilityRole = Platform.OS === 'web' ? 'list' : 'tablist';
  const stepAccessibilityRole = Platform.OS === 'web' ? 'listitem' : 'tab';
  return /*#__PURE__*/_jsx(View, {
    ref: ref,
    style: selectContainerStyles(themeTokens),
    ...selectedProps,
    children: /*#__PURE__*/_jsxs(StackView, {
      space: 0,
      children: [/*#__PURE__*/_jsx(View, {
        style: [staticStyles.stepsContainer, selectStepsContainerStyles(themeTokens)],
        accessibilityRole: stepsContainerAccessibilityRole,
        children: steps.map((label, index) => {
          return /*#__PURE__*/_jsx(Step, {
            status: current,
            label: label,
            name: getStepLabel(index),
            stepIndex: index,
            stepCount: steps.length,
            tokens: themeTokens,
            accessibilityRole: stepAccessibilityRole,
            accessibilityCurrent: current === index && Platform.OS === 'web' && 'step',
            isBarVariant: isBarVariant
          }, label);
        })
      }), showStepTrackerLabel && /*#__PURE__*/_jsx(View, {
        style: [staticStyles.stepTrackerLabelContainer, selectStepTrackerLabelContainerStyles(themeTokens)],
        children: /*#__PURE__*/_jsx(Text, {
          style: selectStepTrackerLabelStyles(themeTokens, themeOptions),
          accessibilityRole: "header",
          accessibilityLevel: 2,
          children: stepTrackerLabel
        })
      })]
    })
  });
});
StepTracker.displayName = 'StepTracker';

// If a language dictionary entry is provided, it must contain every key
const dictionaryContentShape = PropTypes.shape({
  stepLabel: PropTypes.string.isRequired,
  stepTrackerLabel: PropTypes.string.isRequired
});
StepTracker.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * The current step, 0-based number
   */
  current: PropTypes.number,
  /**
   * The language to use for the labels
   */
  copy: PropTypes.oneOfType([PropTypes.oneOf(['en', 'fr']), dictionaryContentShape]),
  /**
   * Custom dictionary containing the labels to use for the steps
   */
  dictionary: PropTypes.shape({
    en: dictionaryContentShape,
    fr: dictionaryContentShape
  }),
  /**
   * An array of strings defining the step titles
   */
  steps: PropTypes.arrayOf(PropTypes.string),
  tokens: getTokensPropType('StepTracker'),
  variant: variantProp.propType
};
export default StepTracker;
const staticStyles = StyleSheet.create({
  stepsContainer: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    flexGrow: 1
  },
  stepTrackerLabelContainer: {
    textAlign: 'center',
    flexDirection: 'row',
    justifyContent: 'center'
  }
});